/**
 * Centralized API Error Handler for Monutrack
 *
 * Handles AWS Amplify API errors globally, with special handling for:
 * - NotAuthorizedException (token expiry/inactive)
 * - Session expiry
 * - 401/403 authentication errors
 *
 * Architecture: Three-layer defense
 * Layer 1: API Response Interceptor (this file)
 * Layer 2: Enhanced Error Boundary
 * Layer 3: Auth State Sync
 */

import { signOut, fetchAuthSession } from 'aws-amplify/auth'
import { toast } from 'sonner'

/**
 * Error types we handle specially
 */
export type AuthErrorType =
  | 'NotAuthorizedException'
  | 'UnauthorizedException'
  | 'ForbiddenException'
  | 'TokenExpiredException'

/**
 * Check if error is an authentication-related error
 */
export function isAuthError(error: any): boolean {
  // Check for Amplify/Cognito error types
  if (error?.__type) {
    const errorType = error.__type as string
    return (
      errorType === 'NotAuthorizedException' ||
      errorType === 'UnauthorizedException' ||
      errorType === 'ForbiddenException' ||
      errorType === 'TokenExpiredException'
    )
  }

  // Check for HTTP status codes
  if (error?.response?.status || error?.status || error?.statusCode) {
    const status = error.response?.status || error.status || error.statusCode
    return status === 401 || status === 403
  }

  // Check error message patterns
  if (error?.message) {
    const message = error.message.toLowerCase()
    return (
      message.includes('token is inactive') ||
      message.includes('not authorized') ||
      message.includes('unauthorized') ||
      message.includes('session expired') ||
      message.includes('invalid token')
    )
  }

  return false
}

/**
 * Extract meaningful error message from error object
 */
export function getErrorMessage(error: any): string {
  if (error?.message) return error.message
  if (error?.__type === 'NotAuthorizedException') {
    return 'Sua sessão expirou. Por favor, faça login novamente.'
  }
  if (error?.response?.data?.message) return error.response.data.message
  return 'Erro desconhecido. Por favor, tente novamente.'
}

/**
 * Attempt to refresh the Cognito session tokens
 * Returns true if refresh succeeded, false otherwise
 */
export async function attemptTokenRefresh(): Promise<boolean> {
  try {
    console.log('[AuthError] Attempting token refresh...')
    const session = await fetchAuthSession({ forceRefresh: true })

    if (session.tokens?.accessToken) {
      console.log('[AuthError] Token refresh succeeded')
      return true
    }

    console.log('[AuthError] Token refresh returned no tokens')
    return false
  } catch (refreshError) {
    console.error('[AuthError] Token refresh failed:', refreshError)
    return false
  }
}

/**
 * Handle authentication errors by:
 * 1. First attempting to refresh the session (for 401 errors)
 * 2. Showing a notification to the user (instead of logging out)
 *
 * @param error - Error object from API call
 * @param tenantDomain - Current tenant domain for redirect (optional)
 * @param skipRefresh - Skip token refresh attempt (use when refresh already failed)
 * @returns Promise that resolves when handling is complete. Returns true if refresh succeeded.
 */
export async function handleAuthError(
  error: any,
  tenantDomain?: string,
  skipRefresh = false
): Promise<boolean> {
  const errorMessage = getErrorMessage(error)
  const status = error?.response?.status || error?.status || error?.statusCode

  console.error('[AuthError] Authentication error detected:', {
    type: error?.__type,
    message: errorMessage,
    status,
    tenantDomain,
  })

  // For 401 errors (unauthorized), try refreshing the token first
  // 403 errors (forbidden) are permission issues, not token issues - don't refresh
  if (status === 401 && !skipRefresh) {
    const refreshed = await attemptTokenRefresh()
    if (refreshed) {
      // Token refreshed successfully - caller should retry the request
      return true
    }
    // Refresh failed - show notification instead of logging out
    console.log('[AuthError] Token refresh failed, showing notification')
    toast.error('Sua sessão expirou', {
      description: 'Por favor, tente novamente ou recarregue a página.'
    })
    return false
  }

  // For 403 errors, show a permission error notification
  if (status === 403) {
    toast.error('Acesso negado', {
      description: 'Você não possui permissão para acessar este recurso.'
    })
    return false
  }

  // For other auth errors, show a generic notification
  toast.error('Erro de autenticação', {
    description: errorMessage
  })

  return false
}

/**
 * Wrap an API call with authentication error handling
 *
 * Usage:
 * ```typescript
 * const result = await withAuthErrorHandler(
 *   async () => get({ apiName: 'monumenta', path: '/employees' }),
 *   tenantDomain
 * )
 * ```
 */
export async function withAuthErrorHandler<T>(
  apiCall: () => Promise<T>,
  tenantDomain?: string
): Promise<T> {
  try {
    return await apiCall()
  } catch (error) {
    // Check if this is an authentication error
    if (isAuthError(error)) {
      await handleAuthError(error, tenantDomain)
      // This line won't execute because handleAuthError redirects,
      // but TypeScript needs it for type safety
      throw error
    }

    // Re-throw non-auth errors
    throw error
  }
}

/**
 * Enhanced retry wrapper that handles auth errors
 * Combines retry logic with auth error detection
 *
 * @param fn - Async function to execute
 * @param options - Retry options
 * @param tenantDomain - Tenant domain for redirect
 */
export async function withAuthRetry<T>(
  fn: () => Promise<T>,
  options: {
    maxRetries?: number
    delayMs?: number
  } = {},
  tenantDomain?: string
): Promise<T> {
  const { maxRetries = 2, delayMs = 500 } = options
  let lastError: any

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn()
    } catch (error) {
      lastError = error

      // Don't retry auth errors - handle immediately
      if (isAuthError(error)) {
        await handleAuthError(error, tenantDomain)
        throw error // Won't execute due to redirect
      }

      // Check if we should retry
      const shouldRetry = attempt < maxRetries && isRetryableError(error)

      if (!shouldRetry) {
        throw error
      }

      // Wait before retrying with exponential backoff
      const delay = delayMs * Math.pow(2, attempt)
      await new Promise(resolve => setTimeout(resolve, delay))
    }
  }

  throw lastError
}

/**
 * Check if error is retryable (network errors, 5xx, 429)
 */
function isRetryableError(error: any): boolean {
  // Network errors
  if (error?.name === 'NetworkError' || error?.message?.includes('fetch')) {
    return true
  }

  // HTTP status codes that should be retried
  const status = error?.response?.status || error?.status || error?.statusCode
  if (status) {
    return status === 429 || status >= 500
  }

  return false
}
